import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useAuth } from '../../shared/hooks/useApi/useAuth';
import { WorkViewerInfo } from '../../shared/component/headless/input/InputBody';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { AlertPosition, AlertType } from '../../shared/enum/EnumRepository';
import { useTextListActions } from '../../shared/hooks/useApi/useTextList';
import { useTextViewStore } from '../../shared/store/TextViewStore';
import { useValidation } from '../../shared/hooks/useValidation';
import MoleculeInputDiv from '../../shared/component/molecule/MoleculeInputDiv';
import { INPUT_YEAR_MIN_LENGTH, INPUT_YEAR_MAX_LENGTH } from '../../shared/constants/limits';
import HintWrapper from '../../shared/component/headless/button/HintWrapper';
import { getHintMessage } from '../../shared/constants/hintMessages';
import confirmIcon2 from '../../asset/icon/confirm2.png';
import confirmIcon3 from '../../asset/icon/confirm3.png';
import deleteOpenIcon from '../../asset/icon/delete_open.png';
import deleteCloseIcon from '../../asset/icon/delete_close.png';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import MoleculeDivToggle from '../../shared/component/molecule/MoleculeDivToggle';
import { useTextTagStore } from '../../shared/store/textTagStore';
import { usePageOwnerIdentityStore } from '../../shared/store/pageOwnerIdentityStore';
import { canShowTextTagList } from '../../shared/util/visibility/textTagVisibility';

const MultiTextViewer: React.FC = () => {
  const { isOwner } = useAuth();
  const { isEditMode } = useEditMode();
  const { enqueueAlert } = useStandardAlertStore();
  const multiSelectedText = useTextViewStore((s) => s.multiSelectedText);
  const multiHasChanged = useTextViewStore((s) => s.multiHasChanged);
  const setMultiHasChanged = useTextViewStore((s) => s.setMultiHasChanged);
  const { updateTextBulk, deleteTextsBulk } = useTextListActions();
  const { checkType } = useValidation();
  const tags = useTextTagStore((s) => s.tags);
  const listOwnerAui = useTextTagStore((s) => s.listOwnerAui);
  const tagStatus = useTextTagStore((s) => s.status);
  const pageOwner = usePageOwnerIdentityStore((s) => s.pageOwner);
  const canShowTags = canShowTextTagList({
    pageOwnerAui: pageOwner?.aui,
    listOwnerAui,
    status: tagStatus,
  });
  const visibleTags = canShowTags ? tags : [];

  const [prodYear, setProdYear] = useState<string | null>(null);
  const [author, setAuthor] = useState<string | null>(null);
  const [isPublic, setIsPublic] = useState<boolean | null>(null);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [tagsChanged, setTagsChanged] = useState(false);

  const multiIdsKey = useMemo(
    () => [...multiSelectedText].map((t) => t.id).sort().join('|'),
    [multiSelectedText]
  );

  useEffect(() => {
    const list = useTextViewStore.getState().multiSelectedText;
    if (list.length === 0) return;

    const first = list[0];
    const allSame = <T,>(pick: (w: typeof first) => T) =>
      list.every((t) => pick(t) === pick(first));

    setProdYear(allSame((t) => t.prodYear) ? first.prodYear : null);
    setAuthor(allSame((t) => t.source.author) ? first.source.author : null);
    setIsPublic(allSame((t) => t.isPublic) ? first.isPublic : null);

    const allHaveTags = list.every((t) => t.tags !== undefined);
    if (allHaveTags) {
      const countByTagId = new Map<number, number>();
      for (const text of list) {
        const textTagIds = new Set((text.tags ?? []).map((tag) => tag.id));
        for (const id of textTagIds) {
          countByTagId.set(id, (countByTagId.get(id) ?? 0) + 1);
        }
      }
      setSelectedTagIds(
        [...countByTagId.entries()]
          .filter(([, count]) => count === list.length)
          .map(([id]) => id),
      );
    } else {
      setSelectedTagIds([]);
    }
    setTagsChanged(false);

    setMultiHasChanged(false);
  }, [multiIdsKey, setMultiHasChanged]);

  if (multiSelectedText.length === 0) return null;

  const handleChangeWithValidate = (field: 'prodYear', value: string) => {
    if (!checkType(field, value)) return;
    setProdYear(value);
    setMultiHasChanged(true);
  };

  const handleIsPublicToggle = (value: boolean) => {
    if (!isEditMode || !isOwner) return;
    setIsPublic(value);
    setMultiHasChanged(true);
  };

  const handleTagToggle = (tagId: number) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
    setTagsChanged(true);
    setMultiHasChanged(true);
  };

  const handleBulkUpdate = async () => {
    const rows = useTextViewStore.getState().multiSelectedText;
    if (rows.length === 0) return;
    const textList = rows.map((row) => {
      const yearStr = prodYear !== null ? prodYear : row.prodYear;
      return {
        id: row.id,
        source: {
          ...row.source,
          author: author !== null ? author : row.source.author,
        },
        prodYear: parseInt(yearStr, 10),
        isPublic: isPublic !== null ? isPublic : row.isPublic,
        ...(tagsChanged ? { tagIds: selectedTagIds } : {}),
      };
    });
    await updateTextBulk({ textList });
  };

  const handleBulkDeleteSelectedTexts = () => {
    if (!isOwner || multiSelectedText.length === 0) return;
    const ids = multiSelectedText.map((t) => t.id);
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_DELETE_TEXTS_BULK', { count: ids.length }),
      callBack: async () => {
        await deleteTextsBulk(ids);
      },
    });
  };

  return (
    <MultiTextViewerComp>
      <MultiTitle>{multiSelectedText.length} texts selected</MultiTitle>
      <TextMeta>
        <TextInfoRow>
          <TextInfoLabel>Author</TextInfoLabel>
          <MoleculeInputDiv
            value={author || ''}
            defaultValue={'Author'}
            DefaultDiv={DefaultDiv}
            placeholder={'Author'}
            handleChange={(e) => {
              setAuthor(e.target.value);
              setMultiHasChanged(true);
            }}
            inputStyle={WorkViewerInfo}
            StyledDiv={Info}
          />
        </TextInfoRow>
        <TextInfoRow>
          <TextInfoLabel>Year</TextInfoLabel>
          <MoleculeInputDiv
            value={prodYear || ''}
            defaultValue={'Year'}
            DefaultDiv={DefaultDiv}
            placeholder={'Year'}
            handleChange={(e) => handleChangeWithValidate('prodYear', e.target.value)}
            inputStyle={WorkViewerInfo}
            StyledDiv={Info}
            minLimit={INPUT_YEAR_MIN_LENGTH}
            maxLimit={INPUT_YEAR_MAX_LENGTH}
          />
        </TextInfoRow>
        {isEditMode && (
          <TextInfoTags>
            <TextInfoLabel>Label</TextInfoLabel>
            <TagContainer>
              {visibleTags.map((tag) => (
                <TagItem
                  key={tag.id}
                  $isActive={selectedTagIds.includes(tag.id)}
                  onClick={() => handleTagToggle(tag.id)}
                >
                  {tag.name}
                </TagItem>
              ))}
            </TagContainer>
          </TextInfoTags>
        )}
      </TextMeta>
      {isEditMode && isOwner && (
        <TextAccessToggles>
          <MoleculeDivToggle
            value={isPublic === true}
            name={isPublic === null ? 'Mixed' : isPublic ? 'Public' : 'Private'}
            handleToggle={(e) => handleIsPublicToggle(e.target.checked)}
          />
        </TextAccessToggles>
      )}
      {isEditMode && isOwner && (
        <MultiActionsRow>
          {multiHasChanged && (
            <HintWrapper content={getHintMessage('UPDATE_SELECTED_TEXTS')} toLeft={true}>
              <UniversalIconBtn
                isPrimary={true}
                size="fit"
                border={true}
                backgroundImg={confirmIcon2}
                backgroundImgHover={confirmIcon3}
                onClick={() => void handleBulkUpdate()}
              />
            </HintWrapper>
          )}
          <HintWrapper content={getHintMessage('DELETE_SELECTED_TEXTS')} toLeft={true}>
            <UniversalIconBtn
              size="fit"
              border={true}
              backgroundImg={deleteCloseIcon}
              backgroundImgHover={deleteOpenIcon}
              onClick={handleBulkDeleteSelectedTexts}
            />
          </HintWrapper>
        </MultiActionsRow>
      )}
    </MultiTextViewerComp>
  );
};

const TextAccessToggles = styled.div`
  display: flex;
  flex-direction: column;
`;

export default MultiTextViewer;

const MultiTextViewerComp = styled.section`
  position: relative;
  width: 100%;
  box-sizing: border-box;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
  padding-bottom: 10px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const MultiTitle = styled.h2`
  width: 100%;
  min-height: 28px;
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_02_1};
`;

const Title = styled.h2`
  width: 100%;
  height: 28px;
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_02_1};
`;

const TitleDefaultDiv = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  color: ${({ theme }) => theme.colors.color_Gray_05} !important;
  ${({ theme }) => theme.typography.Body_02_1};
  background-color: ${({ theme }) => theme.colors.color_Gray_06};
`;

const TextMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const Info = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.2;
`;

const DefaultDiv = styled.div`
  width: fit-content;
  color: ${({ theme }) => theme.colors.color_Gray_05} !important;
  ${({ theme }) => theme.typography.Body_04};
  background-color: ${({ theme }) => theme.colors.color_Gray_06};
`;

const TextInfoRow = styled.div`
  height: 18px;
  min-height: 18px;
  margin-bottom: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const TextInfoLabel = styled.span`
  min-width: 70px;
  padding-top: 2px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  font-weight: 500;
`;

const TextInfoTags = styled.div`
  height: fit-content;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const TagContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 2px;
`;

const TagItem = styled.div<{ $isActive: boolean }>`
  cursor: pointer;
  width: fit-content;
  border-bottom: 1px dotted
    ${({ $isActive, theme }) => ($isActive ? theme.colors.color_Gray_03 : 'transparent')};
  color: ${({ $isActive, theme }) => ($isActive ? theme.colors.color_Gray_03 : theme.colors.color_Gray_04)};
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0.5)};
  &:hover {
    transform: scale(0.95);
  }
  transition: all 0.3s ease-in-out;
`;

const MultiActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5vh;
  margin-top: 8px;
`;
